import { tone } from '../theme';

export const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Shared expiry math for compliance docs (annual fee / insurance / roadworthiness),
// keyed by month+year the same way functions/index.js:430-434 tracks them server-side.

export function expiryInfo(expiryMonth, expiryYear) {
  if (!expiryMonth || !expiryYear) return null;
  const expiryDate = new Date(expiryYear, expiryMonth - 1, 1);
  const daysUntil = Math.round((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
  const toneName = daysUntil < 0 ? 'danger' : daysUntil < 30 ? 'warning' : 'success';
  const label = `${MONTHS_SHORT[expiryMonth - 1]} ${expiryYear}`;
  return { daysUntil, toneName, color: tone(toneName).color, label };
}

// ── CSV export ───────────────────────────────────────────

export function toCsv(rows, columns) {
  const escape = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map(c => escape(c.label)).join(',');
  const body = rows.map(row => columns.map(c => escape(c.value(row))).join(',')).join('\n');
  return `${header}\n${body}`;
}

export function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function toDateSafe(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();          // Firestore Timestamp
  if (v instanceof Date) return v;
  if (typeof v === 'number') return new Date(v);                  // epoch millis
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000); // plain {seconds,...}
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Shows why a record was rejected, revoked or put on hold.
 *
 * The admin is required to type a reason for these actions (see ConfirmModal's
 * requireReason), and it was being written to Firestore but never displayed
 * anywhere — so the record existed and nobody could read it. `when` is optional
 * and accepts any shape toDateSafe handles.
 */

export function fmtDate(v) {
  const d = toDateSafe(v);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

/** Days-ago presets plus an explicit from/to range. `null` days = no limit. */

export const DATE_PRESETS = [
  { key: 'all', label: 'All time', days: null },
  { key: '7',   label: 'Last 7 days',  days: 7 },
  { key: '30',  label: 'Last 30 days', days: 30 },
  { key: '90',  label: 'Last 90 days', days: 90 },
];

/**
 * Returns a predicate for a date range. Records with no usable date are
 * excluded whenever a range is active — they cannot be shown to fall inside
 * one, and silently including them would inflate the counts.
 */

export function makeDateFilter(field, { preset, from, to }) {
  const presetDays = DATE_PRESETS.find(p => p.key === preset)?.days ?? null;
  let start = null;
  let end = null;

  // Build local dates from the YYYY-MM-DD the picker gives us. `new Date(str)`
  // would parse it as UTC midnight and then setHours would shift it into local
  // time — a day early anywhere west of Greenwich.
  const parseYMD = (s, endOfDay) => {
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return null;
    return endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
  };

  if (presetDays !== null) {
    start = new Date();
    start.setDate(start.getDate() - presetDays);
    start.setHours(0, 0, 0, 0);
  }
  if (from) start = parseYMD(from, false) || start;
  // `to` is inclusive of the whole day the admin picked.
  if (to) end = parseYMD(to, true);

  if (!start && !end) return () => true;

  return (record) => {
    const d = toDateSafe(record[field]);
    if (!d) return false;
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  };
}

export const EMPTY_DATE_RANGE = { preset: 'all', from: '', to: '' };

/**
 * Sorts by a date field. Records with no usable date always sort last in both
 * directions — they have no position on the timeline, and floating them to the
 * top of "Oldest first" would bury the genuinely oldest records under noise.
 */

export function sortByDate(list, field, dir) {
  return [...list].sort((a, b) => {
    const da = toDateSafe(a[field]);
    const dbb = toDateSafe(b[field]);
    if (!da && !dbb) return 0;
    if (!da) return 1;
    if (!dbb) return -1;
    return dir === 'asc' ? da - dbb : dbb - da;
  });
}

export const accountToneName = (status) =>
  status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : 'warning';

export const bookingToneName = (status) =>
  status === 'completed' ? 'success'
  : status === 'cancelled' ? 'danger'
  : status === 'ongoing' ? 'primary'
  : 'warning';

export const RESPONSE_MIN_SAMPLE = 3;

export const RESPONSE_POOR_RATE = 0.5;

export function responseSummary(user) {
  const s = user?.responseStats;
  if (!s || !s.received) return null;
  const answered = (s.approved || 0) + (s.declined || 0);
  const avgMs = s.responseCount ? s.responseMsTotal / s.responseCount : null;
  return {
    received: s.received,
    answered,
    expired: s.expired || 0,
    rate: answered / s.received,
    pct: Math.round((answered / s.received) * 100),
    avgMinutes: avgMs == null ? null : Math.max(1, Math.round(avgMs / 60000)),
    // Below this, one ignored request would read as a damning percentage. Not
    // enough to judge anyone on.
    enough: s.received >= RESPONSE_MIN_SAMPLE,
  };
}

export function isUnresponsiveOwner(user) {
  if (user?.role !== 'owner') return false;
  const r = responseSummary(user);
  return !!r && r.enough && r.rate < RESPONSE_POOR_RATE;
}

export function ratingDate(createdAt) {
  try {
    const d = createdAt?.toDate ? createdAt.toDate() : createdAt ? new Date(createdAt) : null;
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

export const BOOKING_STATUS_LABEL = {
  pending_owner_approval: 'Awaiting Owner Approval',
  pending_payment: 'Pending Payment', pending_pickup_photos: 'Pending Pickup Photos',
  pending_pickup_approval: 'Pending Pickup Approval', ongoing: 'Ongoing',
  pending_return_photos: 'Pending Return Photos', pending_return_confirmation: 'Pending Return Confirmation',
  completed: 'Completed', cancelled: 'Cancelled',
};

// Why a booking was cancelled, in words.
//
// Every code the server can write, plus the ones the app writes itself. A
// booking cancelled before any of this existed simply has no reason on it and
// nothing can recover one, so the panel says so rather than showing a blank.
export const CANCEL_REASON_LABEL = {
  // Either side, before payment.
  cancelled_by_renter: 'Cancelled by the renter',
  cancelled_by_owner: 'Cancelled by the owner',
  declined_by_owner: 'Declined by the owner',
  // Time ran out.
  expired_no_response: 'Expired — the owner never replied',
  expired_unpaid: 'Expired — payment was not confirmed in time',
  // Several requests at once (functions/multiRequest.js).
  owner_chose_another: 'The owner accepted a different renter',
  renter_booked_elsewhere: 'The renter booked another bike first',
  lost_to_earlier_acceptance: 'Another booking was accepted moments earlier',
  bike_taken: 'The bike was booked by someone else',
  duplicate_request: 'Duplicate request',
  too_many_requests: 'Refused — renter already at the request limit',
  renter_already_booked: 'Refused — renter already had a booking',
  // Pre-booking (functions/schedule.js).
  dates_unavailable: 'Refused — the bike was booked around those dates',
  too_far_ahead: 'Refused — beyond the booking window',
  renter_overlap: 'Refused — clashed with the renter’s own booking',
  renter_limit: 'Refused — renter at the limit for bookings at once',
  // Admin.
  admin_override: 'Cancelled by SaikApp admin',
};

/** The cancel reason in words, including the agreed_* family and unknowns. */
export function cancelReasonLabel(reason) {
  if (!reason) return null;
  if (CANCEL_REASON_LABEL[reason]) return CANCEL_REASON_LABEL[reason];
  // Closed by agreement, or after an hour of silence: agreed_<why>.
  if (reason.startsWith('agreed_')) {
    const why = {
      renter_changed_plans: 'the renter changed their plans',
      owner_unavailable: 'the owner could not go ahead',
      bike_problem: 'something was wrong with the bike',
      never_collected: 'the bike was never collected',
      agreed_between_us: 'they agreed to call it off',
      other: 'another reason they gave',
    }[reason.slice('agreed_'.length)];
    return why ? `Closed by agreement — ${why}` : 'Closed by agreement';
  }
  // An unknown code is still better shown than hidden.
  return reason.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

// Bookings store their return as display text — "Thu, September 10, 2026" plus
// "5:00 PM" — because that is how the app writes and shows them. Parsed here
// so the panel can ask whether a rental should already have ended.
export function parseBookingEnd(endDate, endTime) {
  if (!endDate || !endTime) return null;
  const datePart = String(endDate).replace(/^[A-Za-z]+,s*/, '');
  const d = new Date(datePart + ' ' + endTime);
  return Number.isNaN(d.getTime()) ? null : d;
}
export const BOOKING_TERMINAL_STATUSES = ['completed', 'cancelled'];

export const PHOTO_ANGLES = ['Front', 'Back', 'Left Side', 'Right Side'];

// ── Lightbox ─────────────────────────────────────────────
// Full-screen photo viewer for inspecting bike condition before approving.

export const COMPLIANCE_DOC_TYPES = [
  { key: 'annualFee', label: 'Annual Fee', monthField: 'annualFeeExpiryMonth', yearField: 'annualFeeExpiryYear' },
  { key: 'insurance', label: 'Insurance', monthField: 'insuranceExpiryMonth', yearField: 'insuranceExpiryYear' },
  { key: 'roadworthiness', label: 'Roadworthiness', monthField: 'roadworthinessExpiryMonth', yearField: 'roadworthinessExpiryYear' },
];

export const SEGMENT_LABEL = { all: 'Everyone', renters: 'Renters only', owners: 'Owners only' };
